/**
 * Composable pour détecter si l'application est dans une iframe
 * et obtenir l'URL du site parent
 */

/**
 * Origine transmise par le site hôte via `?origin=` sur l'URL de l'iframe.
 *
 * Retenue en mémoire dès le premier appel : le paramètre n'est présent que sur
 * l'URL d'entrée, et le routeur la réécrit ensuite.
 *
 * Sans elle, l'origine n'était déduite que de `window.parent.location` —
 * inaccessible en cross-origin — puis de `document.referrer`. Or Safari en
 * navigation privée supprime le référent des iframes tierces : l'application
 * s'identifiait alors avec sa propre adresse, l'API ne reconnaissait plus le
 * site et le produit n'était jamais chargé.
 */
let hostOriginFromQuery: string | null | undefined;

function readHostOriginFromQuery(): string | null {
    if (hostOriginFromQuery !== undefined) {
        return hostOriginFromQuery;
    }
    try {
        const raw = new URLSearchParams(window.location.search).get('origin');
        hostOriginFromQuery = raw ? decodeURIComponent(raw) : null;
    } catch {
        hostOriginFromQuery = null;
    }
    return hostOriginFromQuery;
}

export function useIframeDetection() {
    /**
     * Vérifie si l'application est dans une iframe
     */
    function isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            // Si on ne peut pas accéder à window.top (CORS), on est probablement dans une iframe
            return true;
        }
    }

    /**
     * Obtient l'URL du parent (si possible)
     * Retourne null si impossible d'accéder (restrictions CORS)
     */
    function getParentUrl(): string | null {
        try {
            // Essayer d'accéder à l'URL du parent directement
            if (window.parent && window.parent.location && window.parent.location.href) {
                return window.parent.location.href;
            }
        } catch (e) {
            // Bloqué par CORS
        }

        if (document.referrer) {
            return document.referrer;
        }

        return null;
    }

    /**
     * Obtient l'URL à utiliser pour l'originUrl
     * - Si dans une iframe : URL du parent (ou referrer)
     * - Sinon : URL actuelle
     */
    function getOriginUrl(): string {
        // L'origine annoncée par l'hôte prime : c'est la seule source fiable
        // quand le navigateur retire le référent (navigation privée) et que
        // `window.parent` est cloisonné par la politique d'origine.
        const declared = readHostOriginFromQuery();
        if (declared) {
            return declared;
        }

        const inIframe = isInIframe();

        if (inIframe) {
            const parentUrl = getParentUrl();
            if (parentUrl) {
                return parentUrl;
            }
        }

        return window.location.href;
    }

    /**
     * Demande l'URL du parent via postMessage
     * Retourne une Promise qui se résout avec l'URL
     */
    function requestParentUrl(): Promise<string> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Timeout: Pas de réponse du parent'));
            }, 5000); // 5 secondes de timeout

            // Écouter la réponse
            const handleMessage = (event: MessageEvent) => {
                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                    if (data.eventType === 'sendParentUrl' && data.url) {
                        clearTimeout(timeout);
                        window.removeEventListener('message', handleMessage);
                        resolve(data.url);
                    }
                } catch (e) {
                    // Ignore les messages mal formés
                }
            };

            window.addEventListener('message', handleMessage);

            // Demander l'URL au parent
            const message = {
                eventType: 'askParentUrl'
            };
            window.parent.postMessage(JSON.stringify(message), '*');
        });
    }

    return {
        isInIframe,
        getParentUrl,
        getOriginUrl,
        requestParentUrl
    };
}
